import { useState } from 'react';

import { CALIBRATED_VOLUME, MAX_VOLUME, MIN_VOLUME, readVolume, writeVolume } from '../audio/volume.ts';
import { t } from '../i18n/t';

/**
 * How loud the tutor speaks, on this device.
 *
 * The level `playback.ts` ships is calibrated against a measured sample, but a
 * measurement cannot know the speaker or the room. This is the trim on top of
 * it, and it lives on the device rather than the account because that is what
 * it is correcting for.
 *
 * Written on every change rather than behind a Save button: there is one
 * value, it is instantly reversible, and a settings screen that can be left
 * with an unsaved slider is a settings screen that silently discards it.
 *
 * `defaultValue` with `useState`, not a controlled input re-read from storage:
 * `readVolume` touches `localStorage`, and doing that on every render of a
 * dragged slider is a synchronous disk read per frame.
 */
export function TutorVolume() {
  const [volume, setVolume] = useState(() => readVolume());

  const change = (next: number) => {
    setVolume(next);
    writeVolume(next);
  };

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body flex flex-col gap-3">
        <h2 className="card-title">{t('volume-title')}</h2>
        <p className="text-base-content/60 text-sm">{t('volume-intro')}</p>
        <input
          type="range"
          className="range range-primary"
          min={MIN_VOLUME}
          max={MAX_VOLUME}
          step={5}
          value={volume}
          aria-label={t('volume-title')}
          // The slider's own number is meaningless to a listener, so the
          // accessible value is said in the words the marks below use.
          aria-valuetext={volume === 0 ? t('volume-silent') : t('volume-value', { volume, max: MAX_VOLUME })}
          onChange={(e) => change(Number(e.target.value))}
        />
        <div className="text-base-content/50 flex justify-between text-xs">
          <span>{t('volume-silent')}</span>
          {/* Marking the calibrated point, so "I have wandered off the tuned
              level" is visible rather than something to rediscover by ear. */}
          <span>{t('volume-recommended', { volume: CALIBRATED_VOLUME })}</span>
          <span>{t('volume-loudest')}</span>
        </div>
        {volume !== CALIBRATED_VOLUME && (
          <button
            type="button"
            className="btn btn-ghost btn-sm self-start min-h-(--hit-min)"
            onClick={() => change(CALIBRATED_VOLUME)}
          >
            {t('volume-reset')}
          </button>
        )}
      </div>
    </div>
  );
}
